"use client";

/**
 * Shared "is this error worth logging?" predicates for the error-capture
 * pipeline — used by BOTH the window-level listeners (error-capture.tsx) and
 * the React error.tsx boundary path (report-boundary-error.ts) so the two
 * never drift on what counts as noise.
 *
 * Two classes of benign, environmental noise were drowning the admin error
 * viewer (see /admin/query → Recent errors):
 *
 *   1. Bare fetch network failures — "Failed to fetch" (Chromium) / "Load
 *      failed" (Safari). These fire whenever a request can't complete:
 *      offline, a network switch, a navigation that aborts an in-flight
 *      request, a backgrounded mobile tab, or Supabase's periodic auth-token
 *      refresh on a flaky connection. A single long-open phone can emit
 *      dozens. They are NOT code defects — a genuinely broken endpoint
 *      surfaces as a visible action failure or a specific message, not a bare
 *      unhandled "Failed to fetch" — so we drop them.
 *
 *   2. ChunkLoadError — the tab references old chunk hashes the new build
 *      replaced on deploy. Recoverable by a one-shot reload; not a defect.
 */

/** Exact browser network-failure messages. Matched tightly (anchored) so a
 *  real error that merely CONTAINS these words still reports. */
const BENIGN_NETWORK_PATTERNS: RegExp[] = [
  /^Failed to fetch$/i, // Chromium / Edge fetch network failure
  /^Load failed$/i, // Safari / WebKit fetch network failure
  /^NetworkError when attempting to fetch resource\.?$/i, // Firefox
  /^The network connection was lost\.?$/i, // iOS WKWebView
  /^The Internet connection appears to be offline\.?$/i, // iOS
  /^cancelled$/i, // Safari aborted request
];

/** True when the message is a bare network-failure string we deliberately do
 *  not report (environmental, not a code defect). */
export function isBenignNetworkNoise(message: string): boolean {
  const m = message.trim();
  return BENIGN_NETWORK_PATTERNS.some((re) => re.test(m));
}

/**
 * True on a local development origin, where nothing should be written to
 * `error_log` - dev noise is not production data.
 *
 * It is a REPORTING rule and nothing else. All three capture paths used to
 * spell this check out themselves and return immediately, which quietly took
 * the chunk-error RECOVERY down with the reporting: locally, a stale-deploy
 * ChunkLoadError parked the app on "Something went wrong" with no reload and
 * no log line, which is indistinguishable from a real crash and cost two
 * debugging sessions chasing exactly that. Living here, next to the thing it
 * is allowed to suppress, makes the ordering visible at every call site.
 */
export function isLocalDevHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
}

/** True when the error is a bundler chunk-load failure (stale deploy). Matches
 *  the proper ChunkLoadError class, the message string, and the various
 *  "Failed to load chunk" / "Loading chunk N failed" phrasings. */
export function isChunkLoadError(message: string, reason?: unknown): boolean {
  return (
    (reason instanceof Error && reason.name === "ChunkLoadError") ||
    /ChunkLoadError/i.test(message) ||
    /Failed to load chunk/i.test(message) ||
    /Loading chunk \S+ failed/i.test(message)
  );
}

// Resets on every fresh page load (the module is re-evaluated). Once we've
// scheduled a reload this page session, the tab is on its way out — every
// further chunk error in the same session is part of the SAME stale-deploy
// burst (a deploy can knock out several lazy chunks at once, firing a cascade
// of ChunkLoadErrors in the same tick), so we skip them all rather than
// logging the cascade. Shared across the window + boundary paths via the
// single module instance.
let reloadScheduledThisSession = false;

/**
 * Decide what to do with a detected chunk-load error. Shared by the window
 * listeners and the React boundary so both behave identically:
 *
 *   "reload" — first chunk error this page session: reload once for the fresh
 *              bundle, and DON'T log (stale deploy, recoverable).
 *   "skip"   — a reload is already scheduled this session (same burst): skip
 *              silently so the cascade doesn't spam the log.
 *   "report" — a FRESH page load STILL hits a chunk error within 60s of the
 *              last reload attempt (sessionStorage survives the reload): the
 *              stale-deploy theory is wrong, it's a real network/CDN failure,
 *              so let it reach error_log.
 */
export function classifyChunkError(): "reload" | "skip" | "report" {
  if (reloadScheduledThisSession) return "skip";
  try {
    const KEY = "chunk-reload-attempted-at";
    const WINDOW_MS = 60_000;
    const last = Number(window.sessionStorage.getItem(KEY) ?? "0");
    if (Date.now() - last < WINDOW_MS) return "report";
    window.sessionStorage.setItem(KEY, String(Date.now()));
    reloadScheduledThisSession = true;
    return "reload";
  } catch {
    // sessionStorage blocked (private mode) — don't reload-loop; report.
    return "report";
  }
}
